import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Icon, IconButton } from '@hevoio/hevo-ui-toolkit';
import SearchArea from '../../../list/components/search-area/SearchArea';
import { StickyProgressBar } from '../../../progress-indicators/components/sticky-progress-bar/StickyProgressBar';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import { HdDropdownWithExternalTrigger } from '../../../dropdown/components/dropdown-with-external-trigger/DropdownWithExternalTrigger';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import useUserService from '../../../auth/hooks/useUserService';
import useDebounce from '../../../core/hooks/useDebounce';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { matchPattern } from '../../../core/utils/string';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { getResolvedRoles } from './utils';
import { MembersFactory } from '../factory';
import { Member, Role } from '../models';
import TeamAPI from '../api/TeamAPI';
import { RbacPermissions } from '../../../core/interfaces/user';
import { MembersTrackingActions } from './tracking';
import { isPromiseCancelError } from '../../../core/utils/promise';
import InviteSection from './InviteSection';
import MembersList from './MembersList';
import MembersListShimmer from './MembersListShimmer';
import TeamName from './TeamName';
import styles from './styles.module.scss';

const membersDataIdGenerator = getDataIdGenerator('members');

export interface TeamMembersTabProps {
  roles: Role[];
  rolesLoading: boolean;
  teamName: string;
  onTeamNameUpdate: (name: string) => void;
}

export function TeamMembersTab({
  roles,
  rolesLoading,
  teamName,
  onTeamNameUpdate
}: TeamMembersTabProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersAPIError, setMembersAPIError] = useState<any>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [showRolesFilterMenu, setShowRolesFilterMenu] = useState(false);

  const abortControllerRef = useRef(new AbortController());
  // Keep roles accessible in async callbacks without stale closure issues
  const rolesRef = useRef<Role[]>(roles);
  const didInitialFetch = useRef(false);

  const { isCurrentUserOwner } = useUserService();
  const { hasPermission, isRbacEnabled } = useHasPermission();
  const { eventTrack } = useAnalyticsTracker();
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    rolesRef.current = roles;
  }, [roles]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      eventTrack({
        action: MembersTrackingActions.SEARCH_ACTION,
        properties: { list: members.length }
      });
    }
  }, [debouncedSearchTerm]);

  const fetchData = useCallback(() => {
    setLoadingMembers(true);
    setMembersAPIError(undefined);
    setMembers([]);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    TeamAPI.getMembers(abortControllerRef.current.signal)
      .then(
        res => setMembers(MembersFactory(res.data, rolesRef.current)),
        err => {
          if (!isPromiseCancelError(err)) setMembersAPIError(err);
        }
      )
      .finally(() => setLoadingMembers(false));
  }, []);

  // Trigger initial member fetch once roles are available from the parent
  useEffect(() => {
    if (rolesLoading || didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchData();
  }, [rolesLoading, fetchData]);

  const onInviteSuccess = useCallback(() => fetchData(), [fetchData]);

  const onMemberDelete = useCallback(
    (deletedMember: Member) => setMembers(prev => prev.filter(m => m.id !== deletedMember.id)),
    []
  );

  const onMemberTFADisable = useCallback(
    (member: Member) =>
      setMembers(prev =>
        prev.map(m => (m.id === member.id ? { ...m, isTFAEnabled: false } : { ...m }))
      ),
    []
  );

  const onMemberRoleChange = useCallback(
    (member: Member) =>
      setMembers(prev => prev.map(m => (m.id === member.id ? { ...member } : { ...m }))),
    []
  );

  const updateUserRoles = useCallback(
    (memberId: number, selectedRoles: Role[]) =>
      setMembers(prev =>
        prev.map(m => (m.id !== memberId ? { ...m } : { ...m, rbacRoles: selectedRoles }))
      ),
    []
  );

  const filteredMembers = useMemo(
    () =>
      members.filter(member => {
        const { allAvailableRolesSet } = getResolvedRoles(roles, member.rbacRoles);
        return (
          !member.is_hidden &&
          matchPattern(member.displayName, searchTerm) &&
          (filteredRoles.length === 0 ||
            !!filteredRoles.find(role => allAvailableRolesSet.has(role.roleName)))
        );
      }),
    [members, roles, searchTerm, filteredRoles]
  );

  const canUpdateSelfRoles = useMemo(
    () =>
      members.filter(
        member => !member.is_hidden && member.rbacRoles.find(role => role.roleName === 'TEAM_ADMIN')
      ).length > 1,
    [members]
  );

  const filterClickHandler = () => setShowRolesFilterMenu(isOpen => !isOpen);

  return (
    <>
      <div className={`box__header box__header--progress-bar ${styles.boxHeader}`}>
        <span className='box__title'>
          <TeamName name={teamName} onNameUpdate={onTeamNameUpdate} />
        </span>

        <InviteSection roles={roles} onInviteSuccess={onInviteSuccess} />

        {members.length && loadingMembers ? (
          <div className='box__header__progress-bar'>
            <StickyProgressBar />
          </div>
        ) : null}
      </div>

      {!!members?.length && (
        <div className={styles.inviteSection}>
          <div className={styles.label}>
            <span>{filteredMembers.length} User(s) found</span>
          </div>

          <div className={styles.inviteInputContainer}>
            <SearchArea
              dataId={membersDataIdGenerator('')}
              collapsible
              defaultExpanded={false}
              onSearch={setSearchTerm}
              placeholder='Search Members'
            />

            {hasPermission(RbacPermissions.TEAM_EDIT) && isRbacEnabled && (
              <IconButton
                id='rolesMenuFilter'
                className='mr-2 active'
                onClick={filterClickHandler}
                dataId={membersDataIdGenerator('filter')}
                label='Filter by role'
              >
                <Icon
                  name='filter'
                  className={clsx(filteredRoles.length > 0 && 'active')}
                  size={3}
                />
              </IconButton>
            )}

            <HdDropdownWithExternalTrigger
              dataId={membersDataIdGenerator('')}
              id='rolesMenuFilterDropdown'
              placement='bottom-end'
              onChangeEventHandler={selectedRoles => setFilteredRoles(selectedRoles)}
              onClose={() => setShowRolesFilterMenu(false)}
              open={showRolesFilterMenu}
              displayAccessor='title'
              valueAccessor='roleName'
              selected={filteredRoles}
              multiple
              group
              options={roles}
              target='#rolesMenuFilter'
            />
          </div>
        </div>
      )}

      <div className='box__body' id={membersDataIdGenerator('')}>
        {!membersAPIError ? (
          <>
            {!loadingMembers && (
              <MembersList
                members={filteredMembers}
                roles={roles}
                canUpdateSelfRoles={canUpdateSelfRoles}
                updateRoles={updateUserRoles}
                isCurrentUserOwner={isCurrentUserOwner}
                onMemberDelete={onMemberDelete}
                onMemberTFADisable={onMemberTFADisable}
                onMemberRoleChange={onMemberRoleChange}
              />
            )}

            {loadingMembers && !members.length ? <MembersListShimmer /> : null}

            {!loadingMembers && !filteredMembers.length ? (
              <div className='no-item-box my-9'>
                <div className='no-item-box-icon-container'>
                  <Icon name='team' className='no-item-box-icon' />
                </div>
                <span className='no-item-box-title'>No Members Found</span>
                <span className='no-item-box-desc'>
                  No Members found for above search criteria.
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <RetryApiAlert
            actionHandler={fetchData}
            error={membersAPIError}
            dataId={membersDataIdGenerator('')}
          />
        )}
      </div>

      {!loadingMembers && members.length ? (
        <ScrollToTopButton scrollViewportId={membersDataIdGenerator('')} />
      ) : null}
    </>
  );
}
